import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { PaymentProviderDialog } from '../components/PaymentProviderDialog';
import { Brain, Baby, HeartHandshake, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  brain: Brain,
  child: Baby,
  heart: HeartHandshake,
};

export const PsychologyHomePage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const [categories, setCategories] = useState([]);
  const [initialPkg, setInitialPkg] = useState(null);
  const [dialogPkg, setDialogPkg] = useState(null);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/psychology/categories`).then((r) => setCategories(r.data || []));
    axios.get(`${API}/psychology/content`).then((r) => setCms(r.data)).catch(() => {});
    axios.get(`${API}/psychology/packages`).then((r) => {
      const initial = (r.data || []).find((p) => p.id === 'psy_initial');
      setInitialPkg(initial || null);
    });
  }, []);

  const startInitialAssessment = () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent('/psychology')}`);
      return;
    }
    if (initialPkg) setDialogPkg(initialPkg);
  };

  const chevron = isRTL ? 'rotate-180' : '';

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 via-white to-white ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* ============ HERO ============ */}
      <section className="pt-10 pb-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-3xl bg-gradient-to-b from-[#0d2c54] to-[#123c73] text-white p-8 md:p-12 shadow-xl text-center relative overflow-hidden"
            data-testid="psychology-hero"
          >
            {cms?.banner_image && (
              <>
                <img
                  src={cms.banner_image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="psychology-hero-image"
                />
                <div className="absolute inset-0 bg-[#0d2c54]/70" />
              </>
            )}
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle at 20% 20%, #ffffff 0%, transparent 60%), radial-gradient(circle at 80% 80%, #4fc3f7 0%, transparent 55%)',
            }} />
            <h1 className="relative text-2xl md:text-3xl font-extrabold leading-snug mb-2" data-testid="psychology-hero-title">
              {isRTL
                ? (cms?.title_ar || 'هل تحتاج إلى تقييم مبدئي لحالتك؟')
                : (cms?.title_en || 'Need an initial assessment for your case?')}
            </h1>
            <p className="relative text-base md:text-lg text-blue-100 mb-6">
              {isRTL
                ? (cms?.subtitle_ar || 'احجز استشارتك النفسية الآن')
                : (cms?.subtitle_en || 'Book your psychology consultation now')}
            </p>
            <button
              onClick={startInitialAssessment}
              className="relative inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-l from-[#4dd0e1] to-[#26c6da] text-white font-extrabold text-lg shadow-lg hover:shadow-2xl hover:scale-[1.02] transition transform active:scale-100"
              data-testid="psychology-book-assessment"
            >
              {isRTL ? (cms?.cta_ar || 'احجز موعد التقييم') : (cms?.cta_en || 'Book Assessment')}
            </button>
          </div>
        </div>
      </section>

      {/* ============ CATEGORY LIST ============ */}
      <section className="pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-extrabold text-[#1e88e5] mb-6" data-testid="psychology-categories-title">
            {t('اختر القسم المناسب لحالتك', 'Choose the section that fits your case', 'Choisissez la section adaptée')}
          </h2>

          <div className="space-y-4">
            {categories.map((c) => {
              const Icon = ICON_MAP[c.icon] || Brain;
              return (
                <Link
                  key={c.id}
                  to={`/psychology/${c.id}`}
                  data-testid={`psychology-category-${c.id}`}
                  className="group flex items-center justify-between gap-4 bg-white rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-blue-50 border-4 border-white shadow ring-1 ring-blue-100 flex items-center justify-center text-[#1e88e5]">
                      <Icon size={28} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg md:text-xl font-extrabold text-[#0d2c54] truncate">
                        {language === 'ar' ? c.title_ar : c.title_en}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {language === 'ar' ? c.subtitle_ar : c.subtitle_en}
                      </p>
                    </div>
                  </div>
                  <ChevronLeft size={22} className={`text-gray-400 group-hover:text-[#1e88e5] shrink-0 ${chevron}`} />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <PaymentProviderDialog
        open={!!dialogPkg}
        onOpenChange={(v) => { if (!v) setDialogPkg(null); }}
        packageId={dialogPkg?.id}
        packageKind="psychology"
        packageNameEn={dialogPkg?.name_en}
        packageNameAr={dialogPkg?.name_ar}
        amount={dialogPkg?.price}
      />
    </div>
  );
};

export default PsychologyHomePage;
